import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Pill, Plus, Search, Clock, Check, X, AlertTriangle,
  Calendar, User, FileText, Loader2, ChevronDown, Edit2,
  Trash2, ClipboardList
} from 'lucide-react';

// =====================================================
// MEDICATION SEARCH COMPONENT
// =====================================================

function MedicationSearch({ onSelect, placeholder = "Search medications..." }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [commonMeds, setCommonMeds] = useState([]);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Load common medications on mount
    const loadCommonMeds = async () => {
      try {
        const response = await api.get('/emr/mar/medications/common');
        setCommonMeds(response.data.medications || []);
      } catch (error) {
        console.error('Failed to load common medications:', error);
      }
    };
    loadCommonMeds();
  }, []);

  useEffect(() => {
    // Click outside to close
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchMedications = async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/emr/mar/medications/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data.medications || []);
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to common meds filter
      const filtered = commonMeds.filter(m => 
        m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.generic?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setResults(filtered.map(m => ({
        brand_name: m.name,
        generic_name: m.generic,
        route: m.route
      })));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowDropdown(true);

    // Debounce search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchMedications(value);
    }, 300);
  };

  const handleSelect = (med) => {
    onSelect({
      medication_name: med.brand_name || med.name,
      generic_name: med.generic_name || med.generic,
      rxcui: med.rxcui,
      route: med.route
    });
    setQuery(med.brand_name || med.name);
    setShowDropdown(false);
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          className="pl-10"
          data-testid="medication-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {showDropdown && (query.length >= 2 || results.length > 0) && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.length === 0 && !loading ? (
            <div className="p-3 text-sm text-gray-500">
              No results found. You can enter a custom medication name.
            </div>
          ) : (
            results.map((med, idx) => (
              <button
                key={idx}
                onClick={() => handleSelect(med)}
                className="w-full px-4 py-2 text-left hover:bg-purple-50 border-b last:border-0"
              >
                <div className="font-medium text-gray-900">{med.brand_name || med.name}</div>
                {med.generic_name && (
                  <div className="text-sm text-gray-500">{med.generic_name}</div>
                )}
                {med.route && (
                  <div className="text-xs text-purple-600">{med.route}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {showDropdown && query.length < 2 && commonMeds.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
          <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-b">
            Common Medications
          </div>
          {commonMeds.slice(0, 15).map((med, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect({ brand_name: med.name, generic_name: med.generic, route: med.route })}
              className="w-full px-4 py-2 text-left hover:bg-purple-50 border-b last:border-0"
            >
              <div className="font-medium text-gray-900">{med.name}</div>
              <div className="text-sm text-gray-500">{med.generic}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================
// MED CHARTING MODAL
// =====================================================

function MedChartingModal({ medication, onClose, onChart, clientId }) {
  const [status, setStatus] = useState('given');
  const [notes, setNotes] = useState('');
  const [site, setSite] = useState('');
  const [bpBefore, setBpBefore] = useState('');
  const [pulseBefore, setPulseBefore] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const chartData = {
        scheduled_time: scheduledTime || null,
        status,
        dose_given: medication.dosage,
        site: site || null,
        notes: notes || null,
        bp_before: bpBefore || null,
        pulse_before: pulseBefore ? parseInt(pulseBefore) : null
      };

      await api.post(`/emr/mar/client/${clientId}/${medication.id}/chart`, chartData);
      toast.success(`${medication.medication_name} charted as ${status}`);
      onChart();
      onClose();
    } catch (error) {
      console.error('Charting error:', error);
      toast.error('Failed to chart medication');
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'given', label: 'Given', color: 'bg-green-100 text-green-800' },
    { value: 'refused', label: 'Refused', color: 'bg-red-100 text-red-800' },
    { value: 'held', label: 'Held', color: 'bg-amber-100 text-amber-800' },
    { value: 'not_available', label: 'Not Available', color: 'bg-gray-100 text-gray-800' },
    { value: 'see_notes', label: 'See Notes', color: 'bg-blue-100 text-blue-800' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-purple-900">Chart Medication</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Medication Info */}
        <div className="bg-purple-50 p-4 rounded-lg mb-4">
          <h4 className="font-medium text-purple-900">{medication.medication_name}</h4>
          <p className="text-sm text-purple-700">
            {medication.dosage} • {medication.route} • {medication.frequency}
          </p>
          {medication.instructions && (
            <p className="text-xs text-purple-600 mt-1">{medication.instructions}</p>
          )}
        </div>

        {/* Status Selection */}
        <div className="mb-4">
          <Label className="text-sm font-medium mb-2 block">Administration Status</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {statusOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setStatus(opt.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                  status === opt.value 
                    ? `${opt.color} border-purple-500` 
                    : 'bg-white border-gray-200 hover:border-purple-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scheduled Time (if applicable) */}
        {medication.scheduled_times?.length > 0 && (
          <div className="mb-4">
            <Label className="text-sm font-medium mb-2 block">Scheduled Time</Label>
            <div className="flex flex-wrap gap-2">
              {medication.scheduled_times.map((time, idx) => (
                <button
                  key={idx}
                  onClick={() => setScheduledTime(time)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    scheduledTime === time
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                  }`}
                >
                  {time}
                </button>
              ))}
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-28 text-sm"
                placeholder="Custom"
              />
            </div>
          </div>
        )}

        {/* Site (for injections) */}
        {['SC', 'IM', 'IV', 'subcutaneous', 'intramuscular', 'intravenous'].some(r => 
          medication.route?.toLowerCase().includes(r.toLowerCase())
        ) && (
          <div className="mb-4">
            <Label className="text-sm font-medium">Injection Site</Label>
            <Input
              value={site}
              onChange={(e) => setSite(e.target.value)}
              placeholder="e.g., Left abdomen, Right deltoid"
              className="mt-1"
            />
          </div>
        )}

        {/* Vitals Before (optional) */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-sm font-medium">BP Before (optional)</Label>
            <Input
              value={bpBefore}
              onChange={(e) => setBpBefore(e.target.value)}
              placeholder="120/80"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Pulse Before (optional)</Label>
            <Input
              type="number"
              value={pulseBefore}
              onChange={(e) => setPulseBefore(e.target.value)}
              placeholder="72"
              className="mt-1"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="mb-4">
          <Label className="text-sm font-medium">Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any observations, client response, or reason for status..."
            rows={3}
            className="mt-1"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="confirm-chart-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Charting...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Chart Medication
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// =====================================================
// ADD MEDICATION MODAL
// =====================================================

function AddMedicationModal({ onClose, onAdd, clientId }) {
  const [formData, setFormData] = useState({
    medication_name: '',
    generic_name: '',
    rxcui: '',
    dosage: '',
    dosage_unit: 'mg',
    route: 'PO',
    frequency: 'QD',
    scheduled_times: [],
    is_prn: false,
    prn_reason: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    prescriber_name: '',
    pharmacy: '',
    instructions: ''
  });
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ frequencies: [], routes: [] });
  const [customTime, setCustomTime] = useState('');
  const [interactions, setInteractions] = useState(null);
  const [checkingInteractions, setCheckingInteractions] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await api.get('/emr/mar/options');
        setOptions(response.data);
      } catch (error) {
        console.error('Failed to load options:', error);
      }
    };
    loadOptions();
  }, []);

  const handleMedSelect = async (med) => {
    setFormData(prev => ({
      ...prev,
      medication_name: med.medication_name,
      generic_name: med.generic_name || '',
      rxcui: med.rxcui || '',
      route: med.route || 'PO'
    }));
    
    // Check for drug interactions
    if (med.medication_name) {
      setCheckingInteractions(true);
      setInteractions(null);
      try {
        const response = await api.post(`/emr/mar/client/${clientId}/check-interactions?medication_name=${encodeURIComponent(med.medication_name)}`);
        if (response.data.interactions_found) {
          setInteractions(response.data);
        }
      } catch (error) {
        console.error('Interaction check failed:', error);
      } finally {
        setCheckingInteractions(false);
      }
    }
  };

  const addScheduledTime = () => {
    if (customTime && !formData.scheduled_times.includes(customTime)) {
      setFormData(prev => ({
        ...prev,
        scheduled_times: [...prev.scheduled_times, customTime].sort()
      }));
      setCustomTime('');
    }
  };

  const removeScheduledTime = (time) => {
    setFormData(prev => ({
      ...prev,
      scheduled_times: prev.scheduled_times.filter(t => t !== time)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.medication_name || !formData.dosage || !formData.route) {
      toast.error('Please fill in required fields');
      return;
    }
    
    // Warn about severe interactions but allow override
    if (interactions?.severe_count > 0) {
      const confirmAdd = window.confirm(
        `WARNING: ${interactions.severe_count} severe drug interaction(s) detected!\n\n` +
        interactions.warnings.filter(w => w.severity === 'severe').map(w => w.message).join('\n') +
        '\n\nAre you sure you want to add this medication?'
      );
      if (!confirmAdd) return;
    }

    setLoading(true);
    try {
      await api.post(`/emr/mar/client/${clientId}`, formData);
      toast.success('Medication added to MAR');
      onAdd();
      onClose();
    } catch (error) {
      console.error('Add medication error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add medication');
    } finally {
      setLoading(false);
    }
  };

  const commonTimes = ['06:00', '08:00', '12:00', '14:00', '18:00', '20:00', '22:00'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl p-6 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-purple-900">Add Medication to MAR</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Medication Search */}
          <div>
            <Label className="text-sm font-medium">Medication Name *</Label>
            <MedicationSearch onSelect={handleMedSelect} />
            {formData.medication_name && (
              <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-800">
                Selected: {formData.medication_name}
                {formData.generic_name && ` (${formData.generic_name})`}
              </div>
            )}
            
            {/* Drug Interaction Warnings */}
            {checkingInteractions && (
              <div className="mt-2 p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="text-sm text-blue-700">Checking for drug interactions...</span>
              </div>
            )}
            
            {interactions && interactions.interactions_found && (
              <div className="mt-2 space-y-2">
                {interactions.severe_count > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <span className="font-semibold text-red-800">
                        {interactions.severe_count} Severe Interaction{interactions.severe_count > 1 ? 's' : ''} Detected!
                      </span>
                    </div>
                    {interactions.warnings.filter(w => w.severity === 'severe').map((warning, idx) => (
                      <div key={idx} className="ml-7 text-sm text-red-700">
                        <p className="font-medium">{warning.message}</p>
                        <p className="text-xs text-red-600">{warning.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {interactions.moderate_count > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">
                        {interactions.moderate_count} Moderate Interaction{interactions.moderate_count > 1 ? 's' : ''}
                      </span>
                    </div>
                    {interactions.warnings.filter(w => w.severity === 'moderate').map((warning, idx) => (
                      <div key={idx} className="ml-7 text-sm text-amber-700">
                        <p className="font-medium">{warning.message}</p>
                        <p className="text-xs text-amber-600">{warning.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Dosage */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Dosage *</Label>
              <Input
                value={formData.dosage}
                onChange={(e) => setFormData(prev => ({ ...prev, dosage: e.target.value }))}
                placeholder="e.g., 500"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Unit</Label>
              <select
                value={formData.dosage_unit}
                onChange={(e) => setFormData(prev => ({ ...prev, dosage_unit: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="mg">mg</option>
                <option value="mcg">mcg</option>
                <option value="g">g</option>
                <option value="ml">ml</option>
                <option value="units">units</option>
                <option value="tablets">tablet(s)</option>
                <option value="capsules">capsule(s)</option>
                <option value="puffs">puff(s)</option>
                <option value="drops">drop(s)</option>
                <option value="patches">patch</option>
              </select>
            </div>
          </div>

          {/* Route and Frequency */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Route *</Label>
              <select
                value={formData.route}
                onChange={(e) => setFormData(prev => ({ ...prev, route: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                {options.routes.map(r => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium">Frequency *</Label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                {options.frequencies.map(f => (
                  <option key={f.code} value={f.code}>{f.code} - {f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* PRN Toggle */}
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
            <Checkbox
              checked={formData.is_prn}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_prn: checked }))}
              id="is_prn"
            />
            <div>
              <Label htmlFor="is_prn" className="font-medium cursor-pointer">PRN (As Needed)</Label>
              <p className="text-xs text-gray-500">Medication is given as needed, not on a schedule</p>
            </div>
          </div>

          {formData.is_prn && (
            <div>
              <Label className="text-sm font-medium">PRN Reason</Label>
              <Input
                value={formData.prn_reason}
                onChange={(e) => setFormData(prev => ({ ...prev, prn_reason: e.target.value }))}
                placeholder="e.g., pain, anxiety, nausea, insomnia"
                className="mt-1"
              />
            </div>
          )}

          {/* Scheduled Times (if not PRN) */}
          {!formData.is_prn && (
            <div>
              <Label className="text-sm font-medium">Scheduled Times</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {commonTimes.map(time => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => {
                      if (formData.scheduled_times.includes(time)) {
                        removeScheduledTime(time);
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          scheduled_times: [...prev.scheduled_times, time].sort()
                        }));
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-sm ${
                      formData.scheduled_times.includes(time)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  type="time"
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  className="w-32"
                />
                <Button type="button" variant="outline" size="sm" onClick={addScheduledTime}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.scheduled_times.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {formData.scheduled_times.map(time => (
                    <span key={time} className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                      {time}
                      <button onClick={() => removeScheduledTime(time)} className="ml-1 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Start Date *</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">End Date (optional)</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>

          {/* Prescriber */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Prescriber</Label>
              <Input
                value={formData.prescriber_name}
                onChange={(e) => setFormData(prev => ({ ...prev, prescriber_name: e.target.value }))}
                placeholder="Dr. Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Pharmacy</Label>
              <Input
                value={formData.pharmacy}
                onChange={(e) => setFormData(prev => ({ ...prev, pharmacy: e.target.value }))}
                placeholder="CVS, Walgreens, etc."
                className="mt-1"
              />
            </div>
          </div>

          {/* Instructions */}
          <div>
            <Label className="text-sm font-medium">Special Instructions</Label>
            <Textarea
              value={formData.instructions}
              onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
              placeholder="e.g., Take with food, Do not crush, Check BP before administering"
              rows={2}
              className="mt-1"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !formData.medication_name || !formData.dosage}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="add-medication-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add to MAR
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// =====================================================
// MAIN MAR SECTION COMPONENT
// =====================================================

export default function MARSection({ clientId }) {
  const { user } = useAuth();
  const [marEntries, setMarEntries] = useState([]);
  const [todaySheet, setTodaySheet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [chartingMed, setChartingMed] = useState(null);
  const [viewMode, setViewMode] = useState('today'); // 'today' or 'all'
  const [setupRequired, setSetupRequired] = useState(false);

  const canManage = user && ['admin', 'super_admin', 'rn'].includes(user.role);
  const canChart = user && ['admin', 'super_admin', 'rn', 'lpn', 'caregiver', 'cna'].includes(user.role);

  const fetchMAR = async () => {
    setLoading(true);
    setSetupRequired(false);
    try {
      const [allResponse, todayResponse] = await Promise.all([
        api.get(`/emr/mar/client/${clientId}?active_only=true`),
        api.get(`/emr/mar/client/${clientId}/today`)
      ]);
      setMarEntries(allResponse.data.entries || []);
      setTodaySheet(todayResponse.data.mar_sheet || []);
    } catch (error) {
      console.error('Failed to fetch MAR:', error);
      const errorDetail = error.response?.data?.detail || '';
      if (error.response?.status === 503 || errorDetail.includes('MAR tables not set up') || errorDetail.includes('client_mar')) {
        setSetupRequired(true);
      } else {
        toast.error('Failed to load medication records');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchMAR();
    }
  }, [clientId]);

  const handleDiscontinue = async (marId, medName) => {
    if (!confirm(`Discontinue ${medName}? This will mark the medication as inactive.`)) return;

    try {
      await api.delete(`/emr/mar/client/${clientId}/${marId}`);
      toast.success(`${medName} discontinued`);
      fetchMAR();
    } catch (error) {
      console.error('Discontinue error:', error);
      toast.error('Failed to discontinue medication');
    }
  };

  const getChartingStatus = (med) => {
    const scheduledCount = med.scheduled_times?.length || 0;
    const chartedCount = med.charted_times?.length || 0;
    
    if (med.is_prn) {
      return { label: 'PRN', color: 'bg-amber-100 text-amber-800' };
    }
    
    if (scheduledCount === 0) {
      return { label: 'No Schedule', color: 'bg-gray-100 text-gray-600' };
    }
    
    if (chartedCount >= scheduledCount) {
      return { label: 'Complete', color: 'bg-green-100 text-green-800' };
    }
    
    return { label: `${chartedCount}/${scheduledCount}`, color: 'bg-blue-100 text-blue-800' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Setup Required Message
  if (setupRequired) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
        <h3 className="font-semibold text-gray-900 text-lg">MAR Setup Required</h3>
        <p className="text-gray-600 mt-2 max-w-md mx-auto">
          The Medication Administration Record (MAR) tables need to be created in your database.
        </p>
        <div className="mt-4 p-4 bg-gray-50 rounded-lg text-left max-w-lg mx-auto">
          <p className="text-sm font-medium text-gray-700 mb-2">To enable MAR:</p>
          <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
            <li>Open your Supabase SQL Editor</li>
            <li>Copy the contents of <code className="bg-gray-200 px-1 rounded">/app/backend/sql/mar_tables.sql</code></li>
            <li>Execute the SQL script</li>
            <li>Refresh this page</li>
          </ol>
        </div>
        <Button onClick={fetchMAR} variant="outline" className="mt-4">
          <Loader2 className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="mar-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Pill className="w-5 h-5 text-purple-600" />
            Medication Administration Record (MAR)
          </h3>
          <div className="flex rounded-lg border overflow-hidden">
            <button
              onClick={() => setViewMode('today')}
              className={`px-4 py-1 text-sm font-medium ${
                viewMode === 'today' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'
              }`}
            >
              Today's MAR
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-1 text-sm font-medium ${
                viewMode === 'all' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'
              }`}
            >
              All Medications
            </button>
          </div>
        </div>
        {canManage && (
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="add-mar-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Medication
          </Button>
        )}
      </div>

      {/* Today's MAR View */}
      {viewMode === 'today' && (
        <Card className="overflow-hidden">
          <div className="bg-purple-50 px-4 py-3 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-600" />
              <span className="font-medium text-purple-900">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <span className="text-sm text-purple-700">
              {todaySheet.length} active medication{todaySheet.length !== 1 ? 's' : ''}
            </span>
          </div>

          {todaySheet.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Pill className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No active medications on the MAR</p>
              {canManage && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  variant="outline"
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Medication
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {todaySheet.map(med => {
                const status = getChartingStatus(med);
                return (
                  <div key={med.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{med.medication_name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {med.dosage} • {med.route} • {med.frequency}
                        </p>
                        {med.instructions && (
                          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {med.instructions}
                          </p>
                        )}
                        
                        {/* Scheduled Times with Charting Status */}
                        {!med.is_prn && med.scheduled_times?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {med.scheduled_times.map((time, idx) => {
                              const isCharted = med.charted_times?.includes(time);
                              return (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                    isCharted
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  <Clock className="w-3 h-3 mr-1" />
                                  {time}
                                  {isCharted && <Check className="w-3 h-3 ml-1" />}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {med.is_prn && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-amber-100 text-amber-800 mt-2">
                            PRN: {med.prn_reason || 'As needed'}
                          </span>
                        )}
                      </div>

                      {/* Chart Button */}
                      {canChart && (
                        <Button
                          onClick={() => setChartingMed(med)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`chart-med-${med.id}`}
                        >
                          <ClipboardList className="w-4 h-4 mr-1" />
                          Chart
                        </Button>
                      )}
                    </div>

                    {/* Recent Charting Records */}
                    {med.charting_records?.length > 0 && (
                      <div className="mt-3 pl-4 border-l-2 border-green-200">
                        <p className="text-xs text-gray-500 mb-1">Today's Charting:</p>
                        {med.charting_records.slice(0, 3).map((record, idx) => (
                          <div key={idx} className="text-xs text-gray-600">
                            {new Date(record.administered_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - 
                            <span className={record.status === 'given' ? ' text-green-600' : ' text-amber-600'}>
                              {' '}{record.status.toUpperCase()}
                            </span>
                            {' by '}{record.charted_by_name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* All Medications View */}
      {viewMode === 'all' && (
        <Card className="p-4">
          {marEntries.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Pill className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No medications in the MAR</p>
            </div>
          ) : (
            <div className="space-y-3">
              {marEntries.map(med => (
                <div key={med.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{med.medication_name}</h4>
                      {med.is_prn && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">PRN</span>
                      )}
                    </div>
                    {med.generic_name && (
                      <p className="text-xs text-gray-500">{med.generic_name}</p>
                    )}
                    <p className="text-sm text-gray-600 mt-1">
                      {med.dosage} {med.dosage_unit} • {med.route} • {med.frequency}
                    </p>
                    {med.prescriber_name && (
                      <p className="text-xs text-gray-500 mt-1">
                        Prescriber: {med.prescriber_name}
                      </p>
                    )}
                    {med.instructions && (
                      <p className="text-xs text-amber-600 mt-1">{med.instructions}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Started: {med.start_date}
                      {med.end_date && ` • Ends: ${med.end_date}`}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {canChart && (
                      <Button
                        onClick={() => setChartingMed(med)}
                        size="sm"
                        variant="outline"
                        className="text-green-600 border-green-300 hover:bg-green-50"
                      >
                        <ClipboardList className="w-4 h-4" />
                      </Button>
                    )}
                    {canManage && (
                      <Button
                        onClick={() => handleDiscontinue(med.id, med.medication_name)}
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Add Medication Modal */}
      {showAddModal && (
        <AddMedicationModal
          clientId={clientId}
          onClose={() => setShowAddModal(false)}
          onAdd={fetchMAR}
        />
      )}

      {/* Charting Modal */}
      {chartingMed && (
        <MedChartingModal
          medication={chartingMed}
          clientId={clientId}
          onClose={() => setChartingMed(null)}
          onChart={fetchMAR}
        />
      )}
    </div>
  );
}
